import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Send, Calendar, MessageCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistantPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [question, setQuestion] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Generate session ID on mount
  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('psn_chat_session');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('psn_chat_session', newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  // Show popup after a short delay when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
      setIsOpen(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    const userMessage = question.trim();
    setQuestion('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      // Add assistant response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      
      // Update session ID if returned
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        sessionStorage.setItem('psn_chat_session', response.data.session_id);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at (954) 778-9764 or info@professionalservicesnationwide.com." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!showPopup) return null;

  // Minimized bubble
  if (isMinimized) {
    return (
      <button
        onClick={() => { setIsMinimized(false); setIsOpen(true); }}
        className="fixed bottom-6 right-6 z-50 bg-blue-800 text-white p-4 rounded-full shadow-2xl hover:bg-blue-900 transition-all duration-300 hover:scale-110 animate-bounce"
        data-testid="ai-assistant-bubble"
      >
        <MessageCircle size={28} />
      </button>
    );
  }

  // Closed state - show floating button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-blue-800 text-white p-4 rounded-full shadow-2xl hover:bg-blue-900 transition-all duration-300 hover:scale-110"
        data-testid="ai-assistant-bubble"
      >
        <MessageCircle size={28} />
      </button>
    );
  }

  return (
    <div 
      className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-slideUp flex flex-col"
      style={{ maxHeight: '80vh' }}
      data-testid="ai-assistant-popup"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white p-4 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-blue-300">
              <span className="text-2xl">👩‍💼</span>
            </div>
            <div>
              <h3 className="font-bold text-lg">Sarah</h3>
              <p className="text-blue-200 text-sm">AI Business Assistant</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="text-blue-200 hover:text-white p-1 transition-colors"
              title="Minimize"
            >
              <span className="text-lg">—</span>
            </button>
            <button
              onClick={() => { setIsOpen(false); setShowPopup(false); }}
              className="text-blue-200 hover:text-white p-1 transition-colors"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Chat Body */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50" style={{ minHeight: '200px', maxHeight: '400px' }}>
        {/* Initial greeting if no messages */}
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-gray-800 text-sm leading-relaxed">
              Hey there! I'm Sarah, your AI Business Assistant from Professional Services Nationwide. 👋
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              I can help you with questions about our bookkeeping, accounting, payroll, business automation, and other services. What can I assist you with today?
            </p>
            
            {/* Quick action buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => setQuestion("What services do you offer?")}
                className="text-xs px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
              >
                Services offered
              </button>
              <button
                onClick={() => setQuestion("How can you help my business?")}
                className="text-xs px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
              >
                How can you help?
              </button>
              <button
                onClick={() => setQuestion("What are your business hours?")}
                className="text-xs px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
              >
                Business hours
              </button>
            </div>
          </div>
        )}

        {/* Chat messages */}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}
          >
            <div
              className={`inline-block max-w-[85%] px-4 py-2 rounded-2xl text-sm ${
                msg.role === 'user'
                  ? 'bg-blue-800 text-white rounded-br-md'
                  : 'bg-white text-gray-800 shadow-sm border border-gray-200 rounded-bl-md'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="text-left mb-3">
            <div className="inline-block px-4 py-2 bg-white rounded-2xl rounded-bl-md shadow-sm border border-gray-200">
              <Loader2 size={18} className="animate-spin text-blue-800" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Footer with input and Book Meeting */}
      <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0">
        {/* Book a Meeting Button */}
        <Link
          to="/contact"
          className="flex items-center justify-center w-full px-4 py-2 mb-3 border-2 border-blue-800 text-blue-800 font-semibold rounded-full hover:bg-blue-800 hover:text-white transition-all duration-200 text-sm"
          data-testid="book-meeting-btn"
        >
          <Calendar size={16} className="mr-2" />
          Book a Meeting
        </Link>

        {/* Question Input */}
        <form onSubmit={handleSendQuestion}>
          <div className="flex items-center bg-gray-100 border border-gray-300 rounded-full overflow-hidden focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question..."
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-transparent text-gray-700 focus:outline-none text-sm disabled:opacity-50"
              data-testid="ai-question-input"
            />
            <button
              type="submit"
              disabled={isLoading || !question.trim()}
              className="px-4 py-2.5 text-blue-800 hover:text-blue-900 transition-colors disabled:opacity-50"
              data-testid="ai-send-btn"
            >
              <Send size={18} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIAssistantPopup;
